<template>
  <div class="event-stream smooth-shadow">
    <h2 class="title">Event Stream</h2>
    <!-- control bar -->
    <!-- <div class="control-bar">
      <icon class="row-icon">filter_list</icon>
      <div class="item">
        <label>Filter Events</label>
        <DropDown class="dropdown" :placeholder="'Filter Events'" />
      </div>
      <expand />
      <span class="checkbox" @click="isLive = !isLive">
        <icon>{{isLive? 'check_box' : 'check_box_outline_blank'}}</icon>Show Live
      </span>
    </div>-->

    <!-- events visualisation area -->
    <div class="stream-area">
      <div class="event-list">
        <h3 class="events-header smooth-shadow">
          <icon>bubble_chart</icon>Event List
        </h3>
        <simplebar class="list-wrapper" data-simplebar-auto-hide="true">
          <div v-if="events && events.length > 0" class="events-wrapper">
            <!-- <div
              v-for="(event, index) in events"
              :key="index"
              :class="['event-item', {'active': index === activeEvent }]"
              @click="activeEvent = index"
            >
              <div class="flex-col flex-expand">
                <label>{{ formatDate(event.timestamp) }}</label>
                <h3 class="ellipsis">{{ event.data[0].value }}</h3>
              </div>
              <icon>keyboard_arrow_right</icon>
            </div>-->
            <EventGroup
              v-for="(group, group_index) in eventGroups"
              :key="group_index"
              :events="group.events"
              :date="group.timestamp"
              :selectedEvent="activeEvent"
              :isActive="activeGroup === group_index"
              @selected="(item_index) => setSelected(group_index, item_index)"
            />
            <div v-if="isMoreDataAvailable && !isLoading" class="flex-col">
              <button
                class="load-more flex-expand glow-shadow"
                @click="$emit('loadMore')"
              >
                <icon size="18">arrow_downward</icon>Load More
              </button>
            </div>
            <div class="loading-spinner">
              <Spinner v-if="isLoading" />
            </div>
          </div>
        </simplebar>
      </div>
      <div class="deck-area" ref="deck-area">
        <DeckGL
          :accessToken="accessToken"
          :layers="[...staticLayers, ...getLayers]"
          :latitude="location[0]"
          :longitude="location[1]"
          :zoom="12"
        />
        <Player
          :play="isPlaying"
          :speed="`${playBackSpeed + 1}x`"
          :progress="playProgress"
          @changeSpeed="changeSpeed"
          @toggle="togglePlay"
          @rewind="rewind"
          @forward="forward"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DeckGL from "@/components/global/views/DeckGL";
import moment from "@/lib/moment";
import { PathLayer, ScatterplotLayer } from "@deck.gl/layers";
import Player from "@/components/entity_overview/views/Player";
import { TripsLayer } from "@deck.gl/geo-layers";
import EventGroup from "@/components/entity_overview/views/EventGroup";
import { mapActions, mapGetters } from "vuex";
import PointLayer from "@/layers/PointLayer";

export default {
  components: {
    DeckGL,
    Player,
    EventGroup,
  },
  props: {
    events: Array,
    staticEntities: Array,
    isMoreDataAvailable: Boolean,
    isLoading: Boolean,
  },
  data() {
    return {
      isLive: false,
      accessToken:
        "pk.eyJ1IjoiaGF4emllIiwiYSI6ImNqZ2c2NWp3OTBxenAzM3FzeThydmZ0YncifQ.584ugILuKFfDPTxqyiO_0g",
      activeEvent: 0,
      activeGroup: 0,
      isPlaying: false,
      speeds: [1500, 1000, 600, 300],
      playBackSpeed: 0,
      playInterval: null,
    };
  },
  computed: {
    /**
     * Groups the events into day
     */
    eventGroups() {
      let groups = {};
      this.events.forEach((event, index) => {
        let eventDate = new Date(event.timestamp);
        let day_id = `${eventDate.getDate()}-${
          eventDate.getMonth() + 1
        }-${eventDate.getFullYear()}`;
        // if the day is present in the group, push the event to that day
        if (groups[day_id]) {
          groups[day_id].events.push(event);
        } else {
          // else create an object with the following structure
          groups[day_id] = {
            timestamp: event.timestamp,
            events: [event],
          };
        }
      });
      return Object.keys(groups).map((item) => ({
        ...groups[item],
        events: groups[item].events.reverse(),
      }));
    },
    // get all events of the active group
    getActiveGroupEvents() {
      let events = this.eventGroups[this.activeGroup].events;
      return events;
    },
    // calculates the percentage of the play
    playProgress() {
      return Math.ceil(
        (this.activeEvent / (this.getActiveGroupEvents.length - 1)) * 100
      );
    },
    // generates the list of layers needs to be renderd for the current group
    getLayers() {
      let lineData = [];
      let pathLayer = null;
      let events = this.getActiveGroupEvents;

      if (!(events && events.length > 0)) return [];

      if (this.isPlaying) {
        const pathsData = events.map((item) => [
          item.location[1],
          item.location[0],
        ]);

        let times = 1000;
        const timesData = events.map((item) => {
          times += 200;
          return times;
        });

        pathLayer = new TripsLayer({
          id: "entity_trip",
          data: [{ pathsData, timesData }],
          getPath: (d) => d.pathsData,
          getTimestamps: (d) => d.timesData,
          getColor: [7, 245, 162],
          opacity: 0.8,
          widthMinPixels: 5,
          rounded: true,
          trailLength: 1500,
          currentTime: 1000 + (this.activeEvent + 1) * 200,
        });
      } else {
        lineData = events.map((item) => [item.location[1], item.location[0]]);

        pathLayer = new PathLayer({
          id: "entity_path",
          data: [{ path: lineData }],
          pickable: false,
          widthScale: 20,
          widthMinPixels: 2,
          getPath: (d) => d.path,
          getColor: (d) => [0, 255, 164],
          getWidth: (d) => 1,
        });
      }

      let activeLocation = events[this.activeEvent].location;
      this.location = activeLocation;
      activeLocation = [activeLocation[1], activeLocation[0]];

      const point = new ScatterplotLayer({
        id: "location-point",
        data: [{ coordinates: activeLocation }],
        pickable: true,
        opacity: 0.8,
        stroked: true,
        filled: true,
        radiusScale: 6,
        radiusMinPixels: 10,
        radiusMaxPixels: 100,
        lineWidthMinPixels: 20,
        getPosition: (d) => d.coordinates,
        getRadius: (d) => 10,
        getFillColor: (d) => [255, 255, 252],
        getLineColor: (d) => [3, 165, 252, 100],
      });

      return [pathLayer, point];
    },
    staticLayers() {
      // static entities
      let layers = [];

      layers = this.staticEntities
        ? this.staticEntities.map((item) => {
            return new PointLayer(
              item.id,
              item.entities,
              item.name,
              () => {}
            ).getLayer();
          })
        : [];

      return layers;
    },
  },
  watch: {
    playBackSpeed() {
      if (this.isPlaying) {
        this.play();
      }
    },
  },
  methods: {
    formatDate(date) {
      return moment(date).format("DD/MM/YY h:mm a");
    },
    changeSpeed() {
      if (this.playBackSpeed < this.speeds.length - 1) {
        this.playBackSpeed++;
      } else {
        this.playBackSpeed = 0;
      }
    },
    setSelected(group_index, item_index) {
      this.activeGroup = group_index;
      this.activeEvent = item_index;
    },
    togglePlay() {
      this.isPlaying = !this.isPlaying;
      if (this.isPlaying) {
        this.play();
      } else {
        this.pause();
      }
    },
    rewind() {
      this.stop();
      this.activeEvent = 0;
    },
    forward() {
      this.stop();
      this.activeEvent = this.getActiveGroupEvents.length - 1;
    },
    play() {
      // clear if there is any play interval going on
      clearInterval(this.playInterval);

      // First Scroll Deck Area into view:
      this.$refs["deck-area"].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // Scroll the first active event into view:
      document
        .querySelector(".events-wrapper .event-item.active")
        .scrollIntoView({ behavior: "smooth", block: "center" });

      this.playInterval = setInterval(() => {
        if (this.activeEvent < this.getActiveGroupEvents.length - 1) {
          this.activeEvent++;

          if (this.activeEvent > 2) {
            // Keep Scrolling current active event into view:
            document
              .querySelector(".events-wrapper .event-item.active")
              .scrollIntoView({ behavior: "smooth", block: "center" });
          }
        } else {
          this.stop();
        }
      }, this.speeds[this.playBackSpeed]);
    },
    pause() {
      clearInterval(this.playInterval);
    },
    stop() {
      this.isPlaying = false;
      clearInterval(this.playInterval);
      this.activeEvent = 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.event-stream {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--border-color);
  padding: 15px 25px;

  .title {
    font-weight: bold;
    margin: 20px 0 10px 0;
  }

  .control-bar {
    display: flex;
    flex-direction: row;
    align-items: center;
    .checkbox {
      display: flex;
      align-items: center;

      &:hover {
        color: var(--color-primary);
        cursor: pointer;
      }

      &:active {
        i {
          transform: scale(0.8);
        }
      }

      i {
        margin-right: 10px;
        pointer-events: none;
        transition: 0.3s all ease-in-out;
      }
    }

    .row-icon {
      padding: 15px;
      //background: var(--border-color);
      border-radius: 50%;
      margin-right: 20px;
    }

    .item {
      display: flex;
      flex-direction: column;

      label {
        margin-bottom: 5px;
      }
      .dropdown {
        width: 200px;
      }
    }
  }

  .stream-area {
    margin-top: 20px;
    display: grid;
    grid-template-columns: 350px 1fr;
    grid-template-rows: 700px;

    .event-list {
      background: var(--color-secondary);
      border-radius: 5px 0 0 5px;
      border: 1px solid var(--border-color);
      display: flex;
      flex-direction: column;

      .events-header {
        background: var(--color-secondary);
        padding: 15px 20px;
        font-weight: bold;
        border-bottom: 1px solid var(--border-color);
        display: flex;
        flex-direction: row;
        align-items: center;
        border-radius: 5px 0 0 0;

        i {
          margin-right: 10px;
        }
      }

      .list-wrapper {
        display: flex;
        height: 100%;
        overflow: auto;
      }

      .load-more {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        flex: 1;
        background: var(--color-primary);
        border: 0;
        margin: 10px;
        padding: 10px 15px;
        border-radius: 5px;
        transition: 0.3s all ease-in-out;

        &:hover {
          cursor: pointer;
          opacity: 0.8;
        }

        &:active {
          transform: scale(0.97);
        }

        i {
          margin-right: 10px;
        }
      }

      .event-item {
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: 15px 20px;
        border-bottom: 1px solid var(--border-color);

        label,
        h3,
        i {
          pointer-events: none;
        }

        label {
          margin-bottom: 5px;
          opacity: 0.7;
        }

        &:hover,
        &.active {
          background: var(--border-color);
          color: var(--color-primary);

          h3 {
            color: var(--color-primary);
          }
          cursor: pointer;

          i {
            display: flex;
          }
        }

        &:first-child {
          border-radius: 5px 0 0 0;
        }

        &:last-child {
          border-radius: 0 0 0 5px;
        }
      }
    }

    .deck-area {
      position: relative;
      border-radius: 0 5px 5px 0;
      overflow: hidden;
    }

    .loading-spinner {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
